/**
 * PreviewView Component
 * 
 * Displays a live preview of the web app hosted in the Parse Server's
 * public folder. The navigation controls are now in the header.
 * 
 * Features:
 * - Iframe-based preview
 * - Loading state handling
 */

import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import GlassLoader from '../../GlassLoader';

interface PreviewViewProps {
  /** URL to load in the iframe */
  previewUrl?: string;
  /** External loading state */
  externalLoading?: boolean;
  /** Signal to force refresh the iframe */
  refreshSignal?: number;
}

const LOAD_TIMEOUT_MS = 12000;

export default function PreviewView({
  previewUrl,
  externalLoading,
  refreshSignal,
}: PreviewViewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const loadTimerRef = useRef<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [address, setAddress] = useState<string>('');

  // Combine internal and external loading states
  const isLoading = useMemo(() => loading || !!externalLoading, [loading, externalLoading]);

  // Handle iframe load completion
  const handleLoad = useCallback(() => {
    if (loadTimerRef.current) {
      window.clearTimeout(loadTimerRef.current);
      loadTimerRef.current = null;
    }
    setLoading(false);
  }, []);

  // Update address when previewUrl changes
  useEffect(() => {
    if (previewUrl) {
      const trimmed = previewUrl.trim();
      if (trimmed) {
        const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
        setAddress(normalized);
      }
    } else {
      setAddress('');
    }
  }, [previewUrl]);

  // Force reload when refreshSignal changes
  useEffect(() => {
    if (!iframeRef.current || !address || refreshSignal === undefined) return;
    const bust = `${address}${address.includes('?') ? '&' : '?'}t=${Date.now()}`;
    setLoading(true);
    iframeRef.current.src = bust;
  }, [refreshSignal, address]);

  // Navigate iframe when address changes
  useEffect(() => {
    if (!iframeRef.current || !address) return;
    setLoading(true);
    iframeRef.current.src = address;

    if (loadTimerRef.current) {
      window.clearTimeout(loadTimerRef.current);
    }
    loadTimerRef.current = window.setTimeout(() => {
      setLoading(false);
    }, LOAD_TIMEOUT_MS);
  }, [address]);

  // No preview URL - show placeholder
  if (!address) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 gap-6">
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/60">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="text-sm font-medium">Awaiting preview</span>
        </div>
        <p className="text-base text-white/40 text-center max-w-md">
          Once your Web App is created, the preview will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Iframe Container */}
      <div className="flex-1 relative bg-black/40">
        {/* Loading Overlay - no backdrop-blur for Safari compatibility */}
        {isLoading && (
          <>
            <div className="absolute inset-0 z-40 bg-black/80" />
            <GlassLoader label="Loading preview…" />
          </>
        )}
        
        {/* Preview Iframe */}
        <iframe
          ref={iframeRef}
          title="App Preview"
          className="w-full h-full border-none"
          style={isLoading ? { pointerEvents: 'none' } : undefined}
          sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-modals allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
          allow="geolocation; camera; microphone; clipboard-read; clipboard-write; fullscreen; autoplay"
          referrerPolicy="origin"
          loading="eager"
          onLoad={handleLoad}
        />
      </div>
    </div>
  );
}
